import { createContext, useContext, useEffect, useState, useCallback } from "react";
import { api, getToken, setToken, setRefreshToken, clearAuth } from "./api.js";

const AuthCtx = createContext(null);

export function AuthProvider({ children }) {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const token = getToken();
    if (!token) {
      setLoading(false);
      return;
    }
    api("/auth/me")
      .then((u) => setUser(u))
      .catch(() => clearAuth())
      .finally(() => setLoading(false));
  }, []);

  // Listen for global auth-expired events emitted by api.js (refresh failed)
  useEffect(() => {
    const onExpired = () => {
      setUser(null);
    };
    window.addEventListener("vorks:auth-expired", onExpired);
    return () => window.removeEventListener("vorks:auth-expired", onExpired);
  }, []);

  const login = useCallback(async (email, password) => {
    const res = await api("/auth/login", {
      method: "POST",
      body: { email, password },
      auth: false,
    });
    setToken(res.token);
    if (res.refresh_token) setRefreshToken(res.refresh_token);
    setUser(res.user);
    return res.user;
  }, []);

  const logout = useCallback(() => {
    clearAuth();
    setUser(null);
  }, []);

  return (
    <AuthCtx.Provider value={{ user, loading, login, logout }}>
      {children}
    </AuthCtx.Provider>
  );
}

export function useAuth() {
  const ctx = useContext(AuthCtx);
  if (!ctx) throw new Error("useAuth must be used inside AuthProvider");
  return ctx;
}
